<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import ToggleButton from 'primevue/togglebutton'
import api from '@/api'
import { useI18n } from '@/i18n'

const { t } = useI18n()

const props = defineProps({
  groupId: { type: Number, required: true }
})

const rows    = ref([])
const loading = ref(false)
const coveredOnly = ref(false)

async function load() {
  if (!props.groupId) return
  loading.value = true
  try {
    const { data } = await api.getGroupWorkflowCoverage(props.groupId, { covered_only: coveredOnly.value })
    rows.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error('[compGroupWorkflowCoverage] fetch error:', e)
    rows.value = []
  } finally {
    loading.value = false
  }
}

onMounted(load)
watch(() => props.groupId, load)
watch(coveredOnly, load)

// ─── Coverage stats ───────────────────────────────────────────────────────────
const stats = computed(() => {
  // One row per (ttp, workflow) pair — collapse to unique TTPs
  const ttps       = new Set(rows.value.map(r => r.ttp_id))
  const covered    = new Set(rows.value.filter(r => r.workflow_id).map(r => r.ttp_id))
  const workflows  = new Set(rows.value.filter(r => r.workflow_id).map(r => r.workflow_id))
  return {
    total:    ttps.size,
    covered:  covered.size,
    gaps:     ttps.size - covered.size,
    workflows: workflows.size,
    pct: ttps.size ? Math.round((covered.size / ttps.size) * 100) : 0,
  }
})

// ─── Table columns ────────────────────────────────────────────────────────────
const columns = [
  { field: 'ttp_tactic',         header: t('hackwatch.ttps.colTactic'),     sortable: true  },
  { field: 'ttp_mitre_id',       header: t('hackwatch.ttps.colMitre'),      sortable: true  },
  { field: 'ttp_name',           header: t('hackwatch.ttps.colTechnique'),  sortable: true  },
  { field: 'workflow_name',      header: 'Workflow',                        sortable: true  },
  { field: 'workflow_severity',  header: 'Severity',                        sortable: true  },
  { field: 'mapping_confidence', header: t('hackwatch.ttps.colConfidence'), sortable: false },
]

function severityTag(sev) {
  const s = String(sev || '').toUpperCase()
  if (s === 'CRITICAL') return 'danger'
  if (s === 'HIGH')     return 'danger'
  if (s === 'MEDIUM')   return 'warning'
  if (s === 'LOW')      return 'info'
  return 'secondary'
}

function confidenceTag(c) {
  const s = String(c || '').toUpperCase()
  if (s === 'HIGH')   return 'success'
  if (s === 'MEDIUM') return 'warning'
  if (s === 'LOW')    return 'secondary'
  return 'secondary'
}
</script>

<template>
  <div class="hw-card">
    <div class="hw-card-header">
      <i class="pi pi-shield hw-card-icon" />
      <span class="hw-card-title">{{ t('hackwatch.groupDetail.legendCoverage') }}</span>
      <ToggleButton
        v-model="coveredOnly"
        :on-label="t('hackwatch.groupDetail.coveredOnly')"
        :off-label="t('hackwatch.groupDetail.allTtps')"
        :on-icon="'pi pi-check-circle'"
        :off-icon="'pi pi-list'"
        class="toggle-btn"
      />
    </div>

    <!-- Coverage KPI bar -->
    <div class="hw-card-body" style="padding-bottom: .5rem;">
    <div class="coverage-bar-wrap mb-3">
      <div class="coverage-stats">
        <span class="stat covered">
          <i class="pi pi-shield mr-1" />{{ stats.covered }} {{ t('hackwatch.groupDetail.covered') }}
        </span>
        <span class="stat gap">
          <i class="pi pi-exclamation-triangle mr-1" />{{ stats.gaps }} {{ t('hackwatch.groupDetail.gaps') }}
        </span>
        <span class="stat workflows">
          <i class="pi pi-sitemap mr-1" />{{ stats.workflows }} {{ t('hackwatch.groupDetail.workflows') }}
        </span>
        <span class="stat pct" :class="stats.pct >= 75 ? 'good' : stats.pct >= 40 ? 'medium' : 'bad'">
          {{ stats.pct }}% Coverage
        </span>
      </div>
      <div class="coverage-progress">
        <div class="progress-fill" :style="{ width: stats.pct + '%' }"
          :class="stats.pct >= 75 ? 'good' : stats.pct >= 40 ? 'medium' : 'bad'" />
      </div>
    </div>
    </div>

    <AppTableWidget
      :columns="columns"
      :data="rows"
      :loading="loading"
      :paginated="true"
      :default-rows="10"
      :rows-options="[10, 25, 50]"
      data-key="ttp_id"
      :empty-text="t('hackwatch.groupDetail.noTtps')"
      @refresh="load()"
    >
      <template #cell-ttp_mitre_id="{ row }">
        <router-link v-if="row.ttp_id" :to="`/threat-intel/mitre/${row.ttp_id}`" class="mitre-link">
          <Tag :value="row.ttp_mitre_id || '—'" severity="secondary" />
        </router-link>
        <Tag v-else :value="row.ttp_mitre_id || '—'" severity="secondary" />
      </template>

      <template #cell-ttp_name="{ row }">
        <router-link v-if="row.ttp_id" :to="`/threat-intel/mitre/${row.ttp_id}`" class="hw-link">
          {{ row.ttp_name }}
        </router-link>
        <span v-else>{{ row.ttp_name }}</span>
      </template>

      <template #cell-workflow_name="{ row }">
        <router-link v-if="row.workflow_id" :to="`/detection/workflows/${row.workflow_id}`" class="hw-link">
          {{ row.workflow_name }}
        </router-link>
        <span v-else class="gap-badge">
          <i class="pi pi-exclamation-triangle mr-1" />{{ t('hackwatch.groupDetail.noWorkflowLinked') }}
        </span>
      </template>

      <template #cell-workflow_severity="{ row }">
        <Tag v-if="row.workflow_severity"
          :value="row.workflow_severity"
          :severity="severityTag(row.workflow_severity)" />
        <span v-else class="muted">—</span>
      </template>

      <template #cell-mapping_confidence="{ row }">
        <Tag v-if="row.mapping_confidence"
          :value="row.mapping_confidence"
          :severity="confidenceTag(row.mapping_confidence)" />
        <span v-else class="muted">—</span>
      </template>
    </AppTableWidget>
  </div>
</template>

<style scoped>
.toggle-btn { font-size: .8rem; height: 28px; }

.coverage-bar-wrap { padding: 0 .25rem; }

.coverage-stats {
  display: flex; flex-wrap: wrap; gap: .75rem;
  margin-bottom: .4rem; font-size: .85rem;
}
.stat { display: flex; align-items: center; }
.stat.covered  { color: var(--p-green-500, #22c55e); }
.stat.gap      { color: var(--p-amber-500, #f59e0b); }
.stat.workflows{ opacity: .75; }
.stat.pct      { font-weight: 700; margin-left: auto; }
.stat.pct.good   { color: var(--p-green-500, #22c55e); }
.stat.pct.medium { color: var(--p-amber-500, #f59e0b); }
.stat.pct.bad    { color: var(--p-red-500, #ef4444); }

.coverage-progress {
  height: 6px; border-radius: 3px;
  background: var(--p-surface-200, rgba(0,0,0,.1));
  overflow: hidden;
}
.progress-fill { height: 100%; border-radius: 3px; transition: width .4s ease; }
.progress-fill.good   { background: var(--p-green-500, #22c55e); }
.progress-fill.medium { background: var(--p-amber-500, #f59e0b); }
.progress-fill.bad    { background: var(--p-red-500, #ef4444); }

.hw-link   { color: var(--p-text-color); text-decoration: none; }
.hw-link:hover { color: var(--p-primary-color); text-decoration: underline; }
.mitre-link{ text-decoration: none; }

.gap-badge { color: var(--p-amber-500, #f59e0b); font-size: .85rem; display: flex; align-items: center; }
.muted     { opacity: .45; }
</style>
